<script setup lang="ts">
import resourcesHero from "@brand-resources-hero"
import { useCycleList, useIntervalFn } from "@vueuse/core"
import { computed, toRef, watch } from "vue"
import { Icon } from "@/components/ui"
import { useTranslation } from "@/composables"

const props = withDefaults(defineProps<ResourcesSectionProps>(), {
  autoplayIntervalMs: 10000,
})

const canvaTemplatesUrlByBrand: Record<string, string> = {
  guestpix: "https://guestpix.com/canva-templates/",
  honorpix: "https://honorpix.com/premium-canva-templates/",
}

function getCanvaTemplatesUrl(): string {
  const brand = import.meta.env.VITE_BRAND || "guestpix"
  return canvaTemplatesUrlByBrand[brand] ?? canvaTemplatesUrlByBrand.guestpix
}

export interface ResourceSectionItem {
  key: string
  imageSrc: string
  imageAlt: string
  title?: string
  description?: string
  href?: string
  ctaText?: string
}

interface ResourcesSectionProps {
  items?: ResourceSectionItem[]
  autoplayIntervalMs?: number
}

const { t } = useTranslation()

const defaultItems = computed<ResourceSectionItem[]>(() => [{
  key: "canva-templates",
  title: t("useful_resources.canva_templates.heading"),
  description: t("useful_resources.canva_templates.description"),
  imageSrc: resourcesHero,
  imageAlt: t("useful_resources.canva_templates.hero_alt"),
  href: getCanvaTemplatesUrl(),
  ctaText: t("useful_resources.canva_templates.cta"),
}])

const displayItems = computed(() => props.items?.length ? props.items : defaultItems.value)

const {
  state: currentItem,
  index: currentIndex,
  next: cycleNext,
  prev: cyclePrev,
  go: cycleGo,
} = useCycleList(displayItems, {
  getIndexOf: (value, list) => list.findIndex(item => item.key === value.key),
})

const showCarouselControls = computed(() => displayItems.value.length > 1)

function hasDetailContent(item: ResourceSectionItem): boolean {
  const hasCta = Boolean(item.href && (item.ctaText || item.title))
  return Boolean(item.title || item.description || hasCta)
}

function advance() {
  if (displayItems.value.length <= 0) {
    return
  }

  cycleNext()
}

function next() {
  advance()
  resetAutoplay()
}

function previous() {
  if (displayItems.value.length <= 0) {
    return
  }

  cyclePrev()
  resetAutoplay()
}

function goTo(index: number) {
  const length = displayItems.value.length
  if (index < 0 || index >= length) {
    return
  }

  cycleGo(index)
  resetAutoplay()
}

const { pause, resume } = useIntervalFn(advance, toRef(props, "autoplayIntervalMs"), { immediate: false })

function resetAutoplay() {
  if (!showCarouselControls.value) {
    return
  }

  pause()
  resume()
}

watch(showCarouselControls, (show) => {
  if (show) {
    resume()
  }
  else {
    pause()
  }
}, { immediate: true })
</script>

<template>
  <div v-if="displayItems.length" class="bg-white" data-test="resources-section">
    <div class="container py-12">
      <h2 class="text-xl font-heading font-bold mb-6" data-test="resources-section-heading">
        {{ t("useful_resources.heading") }}
      </h2>

      <Transition name="gpx-fade" mode="out-in">
        <div
          v-if="currentItem"
          :key="currentItem.key"
          class="flex flex-col md:flex-row"
          data-test="resources-section-slide"
        >
          <div
            class="flex mr-0 mb-5 md:mb-0 md:mr-5"
            :class="{ 'md:flex-1': !hasDetailContent(currentItem) }"
          >
            <img
              :src="currentItem.imageSrc"
              :alt="currentItem.imageAlt"
              class="rounded-xl object-cover aspect-video w-full"
              data-test="resources-section-image"
            >
          </div>

          <div
            v-if="hasDetailContent(currentItem)"
            class="flex flex-col justify-center min-h-60"
            data-test="resources-section-details"
          >
            <h3
              v-if="currentItem.title"
              class="text-lg font-heading font-bold mb-1.5"
              data-test="resources-section-title"
            >
              {{ currentItem.title }}
            </h3>
            <p
              v-if="currentItem.description"
              class="mb-3 font-body"
              data-test="resources-section-description"
            >
              {{ currentItem.description }}
            </p>
            <a
              v-if="currentItem.href && (currentItem.ctaText || currentItem.title)"
              :href="currentItem.href"
              target="_blank"
              rel="noopener noreferrer"
              class="font-body text-link hover:text-link-hover focus:text-link-hover hover:underline focus:underline"
              data-test="resources-section-cta"
            >
              {{ currentItem.ctaText ?? currentItem.title }}
            </a>
          </div>
        </div>
      </Transition>

      <div
        v-if="showCarouselControls"
        class="mt-6 flex items-center justify-center gap-4"
        data-test="resources-section-carousel-controls"
      >
        <button
          type="button"
          class="text-nav-active hover:text-nav-active-hover focus:text-nav-active-hover cursor-pointer"
          :aria-label="t('useful_resources.previous_resource')"
          data-test="resources-section-previous"
          @click="previous"
        >
          <Icon icon="CirclePrevious" />
        </button>

        <div class="flex items-center gap-2">
          <button
            v-for="(item, index) in displayItems"
            :key="item.key"
            type="button"
            class="rounded-full p-1"
            :aria-label="t('useful_resources.go_to_resource', { number: index + 1 })"
            :aria-current="index === currentIndex ? 'true' : undefined"
            :data-test="`resources-section-dot-${index}`"
            @click="goTo(index)"
          >
            <span
              class="block size-2.5 rounded-full transition-colors cursor-pointer"
              :class="index === currentIndex ? 'bg-nav-active' : 'bg-gray-300 hover:bg-gray-400'"
            />
          </button>
        </div>

        <button
          type="button"
          class="text-nav-active hover:text-nav-active-hover focus:text-nav-active-hover cursor-pointer"
          :aria-label="t('useful_resources.next_resource')"
          data-test="resources-section-next"
          @click="next"
        >
          <Icon icon="CircleNext" />
        </button>
      </div>
    </div>
  </div>
</template>
